import { useState, useEffect, useCallback, useRef } from 'react';
import { Clock, Play, Pause, RotateCcw, SkipForward } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { useToolUsage } from '@/hooks/useToolUsage';
import { cn } from '@/lib/utils';

type Phase = 'work' | 'short-break' | 'long-break';
const DURATIONS: Record<Phase, number> = { work: 25 * 60, 'short-break': 5 * 60, 'long-break': 15 * 60 };
const LABELS: Record<Phase, string> = { work: 'Focus Time', 'short-break': 'Short Break', 'long-break': 'Long Break' };

const PomodoroTimer = () => {
  useToolUsage('pomodoro-timer');
  const [phase, setPhase] = useState<Phase>('work');
  const [timeLeft, setTimeLeft] = useState(DURATIONS.work);
  const [isRunning, setIsRunning] = useState(false);
  const [sessions, setSessions] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const total = DURATIONS[phase];
  const progress = ((total - timeLeft) / total) * 100;

  useEffect(() => {
    if (!isRunning) { clearInterval(intervalRef.current); return; }
    intervalRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(intervalRef.current);
          setIsRunning(false);
          // Auto-advance
          if (phase === 'work') {
            setSessions(s => s + 1);
            const nextPhase = (sessions + 1) % 4 === 0 ? 'long-break' : 'short-break';
            toast.success(`Great work! Time for a ${nextPhase === 'long-break' ? 'long' : 'short'} break.`);
            setPhase(nextPhase);
            return DURATIONS[nextPhase];
          } else {
            toast.success('Break over! Ready to focus?');
            setPhase('work');
            return DURATIONS.work;
          }
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [isRunning, phase, sessions]);

  const reset = () => { setIsRunning(false); setTimeLeft(DURATIONS[phase]); };
  const skip = () => {
    setIsRunning(false);
    if (phase === 'work') {
      const next = (sessions + 1) % 4 === 0 ? 'long-break' : 'short-break';
      setPhase(next); setTimeLeft(DURATIONS[next]); setSessions(s => s + 1);
    } else {
      setPhase('work'); setTimeLeft(DURATIONS.work);
    }
  };

  const switchPhase = (p: Phase) => { setPhase(p); setTimeLeft(DURATIONS[p]); setIsRunning(false); };

  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <Clock className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Pomodoro Timer</h1>
          <p className="text-muted-foreground">Stay focused with timed work sessions</p>
        </div>
      </div>

      <div className="flex gap-2 justify-center">
        {(['work', 'short-break', 'long-break'] as Phase[]).map(p => (
          <Button key={p} size="sm" variant={phase === p ? 'default' : 'outline'} onClick={() => switchPhase(p)}>
            {LABELS[p]}
          </Button>
        ))}
      </div>

      <Card className="max-w-md mx-auto">
        <CardContent className="pt-8 pb-8 flex flex-col items-center space-y-6">
          {/* Circular progress */}
          <div className="relative w-52 h-52">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" fill="none" strokeWidth="6" className="stroke-muted" />
              <circle
                cx="50" cy="50" r="45" fill="none" strokeWidth="6"
                className="stroke-primary transition-all duration-1000"
                strokeDasharray={`${progress * 2.827} ${283 - progress * 2.827}`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <p className="text-4xl font-bold tabular-nums">{String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}</p>
              <p className="text-sm text-muted-foreground">{LABELS[phase]}</p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button size="icon" variant="outline" onClick={reset}><RotateCcw className="h-4 w-4" /></Button>
            <Button size="lg" onClick={() => setIsRunning(r => !r)} className="px-8">
              {isRunning ? <><Pause className="h-4 w-4 mr-2" /> Pause</> : <><Play className="h-4 w-4 mr-2" /> Start</>}
            </Button>
            <Button size="icon" variant="outline" onClick={skip}><SkipForward className="h-4 w-4" /></Button>
          </div>

          <p className="text-sm text-muted-foreground">Sessions completed: <span className="font-bold text-primary">{sessions}</span></p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PomodoroTimer;
